/**
 * Identity Alignment Assessment · Results.
 * Route: /identity/results?id=...&email=...
 *
 * Doubles as a public "look-up" page when the visitor doesn't have query
 * params (they enter id + email manually).
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, Download, ExternalLink, Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { identityApi } from "@/lib/identityApi";
import logo from "@/assets/brand/kissed-logo.png";
import { formatApiError } from "@/lib/formatError";

const ARCHETYPE_BADGE = {
  survival: { color: "bg-rose-50 text-rose-900 border-rose-200", label: "Survival Mode" },
  awakening: { color: "bg-amber-50 text-amber-900 border-amber-200", label: "Awakening" },
  rebuilding: { color: "bg-sky-50 text-sky-900 border-sky-200", label: "Rebuilding" },
  becoming: { color: "bg-emerald-50 text-emerald-900 border-emerald-200", label: "Becoming" },
};

export default function IdentityResults() {
  const [params, setParams] = useSearchParams();
  const urlId = params.get("id");
  const urlEmail = params.get("email");

  const [form, setForm] = useState({
    id: urlId || sessionStorage.getItem("kissed_identity_last_submission_id") || "",
    email: urlEmail || sessionStorage.getItem("kissed_identity_last_email") || "",
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchResult = async (id, email) => {
    setLoading(true);
    setData(null);
    try {
      const r = await identityApi.getResult(id, email);
      setData(r.data);
    } catch (err) {
      toast.error(formatApiError(err, "Could not find that report"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (urlId && urlEmail) fetchResult(urlId, urlEmail);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlId, urlEmail]);

  const archetype = data?.result_key || "";
  const badge = useMemo(() => ARCHETYPE_BADGE[archetype] || ARCHETYPE_BADGE.survival, [archetype]);

  const onLookup = (e) => {
    e.preventDefault();
    if (!form.id || !form.email) {
      toast.error("Enter both submission ID and email.");
      return;
    }
    setParams({ id: form.id, email: form.email });
    fetchResult(form.id, form.email);
  };

  const trackBooking = () => {
    if (!data) return;
    identityApi.bookingClick({
      submission_id: data.id,
      archetype: data.result_key,
      booking_url: data.booking_url,
      cta_text: data.cta_text || "Begin My Healing Journey",
    }).catch(() => { /* analytics — fire and forget */ });
  };

  return (
    <div data-testid="identity-results" className="min-h-screen bg-[#faf7f5] text-slate-900">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-3">
          <Link to="/coaching/identity"><img src={logo} alt="KISSED" className="h-11 w-auto drop-shadow-md" /></Link>
          <span className="font-display text-sm text-slate-700">Your Identity Alignment Report</span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10 space-y-8">
        {/* Look-up form (always visible — appears front and center if no query params) */}
        {!data && !loading && (
          <form onSubmit={onLookup} className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="identity-lookup-form">
            <div className="text-[11px] tracking-[0.18em] font-semibold text-purple-900 uppercase">Look up your report</div>
            <h1 className="mt-2 font-display text-2xl text-slate-900">Find my Identity Alignment Report</h1>
            <p className="mt-2 text-sm text-slate-700">
              Enter the submission ID we emailed you, plus the email you used.
            </p>
            <div className="mt-5 grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="lid">Submission ID</Label>
                <Input id="lid" value={form.id} onChange={(e) => setForm({ ...form, id: e.target.value.trim() })} placeholder="sub_…" data-testid="identity-lookup-id" />
              </div>
              <div>
                <Label htmlFor="lemail">Email</Label>
                <Input id="lemail" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value.trim() })} placeholder="you@example.com" data-testid="identity-lookup-email" />
              </div>
            </div>
            <div className="mt-5">
              <Button type="submit" className="bg-purple-900 hover:bg-purple-800 text-white rounded-full px-6" data-testid="identity-lookup-submit">
                <Search className="w-4 h-4 mr-2" /> Find my report
              </Button>
            </div>
          </form>
        )}

        {loading && (
          <div className="text-slate-500 flex items-center"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading your reflection…</div>
        )}

        {data && (
          <>
            {/* Archetype headline */}
            <div className="rounded-2xl border border-slate-200 bg-white p-8" data-testid="identity-result-card">
              <div className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold ${badge.color}`} data-testid="identity-result-badge">
                {badge.label}
              </div>
              <h1 className="mt-4 font-display text-3xl sm:text-4xl text-slate-900 leading-tight" data-testid="identity-result-title">
                {data.result_title}
              </h1>
              {data.description && (
                <p className="mt-4 text-base text-slate-700 leading-relaxed">{data.description}</p>
              )}

              <div className="mt-6 flex flex-wrap gap-3">
                <Button
                  asChild
                  onClick={trackBooking}
                  className="bg-purple-900 hover:bg-purple-800 text-white rounded-full px-6"
                  data-testid="identity-booking-cta"
                >
                  <Link to="/coaching/the-me-i-see/enroll">
                    {data.cta_text || "Begin The Me I See™"} <ArrowRight className="w-4 h-4 ml-1" />
                  </Link>
                </Button>
                <Button asChild variant="outline" className="rounded-full" data-testid="identity-download-pdf">
                  <a href={identityApi.pdfUrl(data.id, data.email)} target="_blank" rel="noreferrer">
                    <Download className="w-4 h-4 mr-2" /> Download my PDF
                  </a>
                </Button>
              </div>
            </div>

            {/* Sections */}
            {data.what_this_may_mean && (
              <section className="rounded-2xl border border-slate-200 bg-white p-6">
                <h2 className="font-display text-xl text-purple-900">What this may mean</h2>
                <div className="mt-3 text-sm text-slate-800 leading-relaxed whitespace-pre-line">
                  {data.what_this_may_mean}
                </div>
              </section>
            )}

            {Array.isArray(data.common_patterns) && data.common_patterns.length > 0 && (
              <section className="rounded-2xl border border-slate-200 bg-white p-6">
                <h2 className="font-display text-xl text-purple-900">Common patterns in this season</h2>
                <ul className="mt-3 space-y-2 text-sm text-slate-800 leading-relaxed">
                  {data.common_patterns.map((p) => (
                    <li key={p} className="flex items-start gap-3">
                      <span className="mt-2 w-1.5 h-1.5 rounded-full bg-purple-900 flex-none" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {data.what_healing_may_require && (
              <section className="rounded-2xl border border-slate-200 bg-white p-6">
                <h2 className="font-display text-xl text-purple-900">What healing may require</h2>
                <div className="mt-3 text-sm text-slate-800 leading-relaxed whitespace-pre-line">
                  {data.what_healing_may_require}
                </div>
              </section>
            )}

            {data.domain_narratives && (
              <section className="rounded-2xl border border-slate-200 bg-white p-6">
                <h2 className="font-display text-xl text-purple-900">Your domains at a glance</h2>
                <div className="mt-4 grid sm:grid-cols-2 gap-4">
                  {Object.values(data.domain_narratives).map((dom) => (
                    <div key={dom.key} className="rounded-xl border border-slate-200 bg-[#faf7f5] p-4">
                      <div className="flex items-center justify-between">
                        <div className="font-semibold text-slate-900">{dom.label}</div>
                        <span className="text-[11px] uppercase tracking-wide text-purple-900 font-semibold">{(dom.band || "").replace("_", " ")}</span>
                      </div>
                      <div className="mt-1 text-xs text-slate-500">Score {dom.score} of {dom.max}</div>
                      <div className="mt-3 text-sm text-slate-800 leading-relaxed">{dom.narrative}</div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {(data.next_step || data.service_blurb) && (
              <section className="rounded-2xl border border-purple-200 bg-purple-50/60 p-6">
                <div className="text-[11px] tracking-[0.18em] font-semibold text-purple-900 uppercase">Your aligned next step</div>
                <h2 className="mt-2 font-display text-2xl text-slate-900">{data.recommended_service || "Identity Alignment Session"}</h2>
                {data.service_blurb && <p className="mt-2 text-sm text-slate-700 leading-relaxed">{data.service_blurb}</p>}
                <div className="mt-5">
                  {data.booking_url && (
                    <Button
                      asChild
                      onClick={trackBooking}
                      className="bg-purple-900 hover:bg-purple-800 text-white rounded-full px-6"
                      data-testid="identity-booking-cta-bottom"
                    >
                      <a href={data.booking_url} target="_blank" rel="noreferrer">
                        {data.cta_text || "Begin My Healing Journey"} <ExternalLink className="w-4 h-4 ml-1" />
                      </a>
                    </Button>
                  )}
                </div>
              </section>
            )}

            <div className="text-xs text-slate-500 text-center">
              Submission ID: <span className="font-mono">{data.id}</span> · saved {data.created_at?.slice(0,10)}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
